// src/pages/newscontent.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Breadcrumbs,
  Link,
  Typography,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { getLangFromCookie } from "../../../../Utils/LangCookie";

// ✅ read() controller wrapper
import { apiReadNews } from "../../../../api/News";
// ✅ use the same URL resolver used by api layer
import { resolvePublicUrl } from "../../../../lib/http";

/* ----------------------------- i18n labels ----------------------------- */

const I18N = {
  home: { ZH: "首頁", EN: "Home", JP: "ホーム" },
  info: { ZH: "最新資訊", EN: "Information", JP: "最新情報" },
  notFoundTitle: {
    ZH: "找不到此消息內容",
    EN: "Content not found",
    JP: "このお知らせは見つかりませんでした",
  },
  notFoundHint: {
    ZH: "請返回「最新資訊」重新選擇。",
    EN: 'Please go back to "Information" and select again.',
    JP: "「最新情報」の一覧に戻って、もう一度お選びください。",
  },
  loadFail: {
    ZH: "無法載入內容",
    EN: "Failed to load content",
    JP: "内容を読み込めませんでした",
  },
};

function safeDecode(seg) {
  try {
    return decodeURIComponent(seg);
  } catch {
    return seg;
  }
}

/**
 * pathname like:
 *  /最新資訊/最新消息/some-slug
 *  /最新資訊/衛生資訊/衛生專欄洗手篇
 *
 * -> newsType = second from last
 * -> slug = last
 */
function pickTypeAndSlugFromPath(pathname = "") {
  const clean = String(pathname || "")
    .split("?")[0]
    .split("#")[0];
  const parts = clean.split("/").filter(Boolean);

  const slugSeg = parts[parts.length - 1] || "";
  const typeSeg = parts[parts.length - 2] || "";

  return {
    slug: safeDecode(slugSeg),
    newsType: safeDecode(typeSeg),
  };
}

/**
 * Rewrite <img src="..."> inside stored WYSIWYG HTML
 * so it works across devices:
 * - http://localhost:xxxx/... -> current API base
 * - /uploads/... -> current API base
 * - uploads/... -> current API base
 */
function rewriteHtmlImageSrc(html) {
  if (!html) return "";
  const s = String(html);

  return s.replace(/\bsrc=(["'])([^"']+)\1/gi, (full, q, src) => {
    return `src=${q}${resolvePublicUrl(src)}${q}`;
  });
}

/** thumbnail helper (fallbacks) */
function pickThumbUrl(n) {
  const raw = n?.thumbnail?.url || n?.thumbnail?.asset?.url || "";
  return resolvePublicUrl(raw);
}

export default function NewsContent({ mode = "b2b" }) {
  const { pathname } = useLocation();

  // language state
  const [lang, setLang] = useState(() => getLangFromCookie() || "EN");
  useEffect(() => {
    const handleLangChange = () => setLang(getLangFromCookie() || "EN");
    window.addEventListener("app:languageChanged", handleLangChange);
    return () =>
      window.removeEventListener("app:languageChanged", handleLangChange);
  }, []);

  const { slug, newsType } = useMemo(
    () => pickTypeAndSlugFromPath(pathname),
    [pathname],
  );

  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");
  const [news, setNews] = useState(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setErrMsg("");

        // ✅ read by slug + newsType
        const data = await apiReadNews({ slug, newsType });

        if (!alive) return;
        setNews(data || null);
      } catch (e) {
        console.error("[NewsContent] apiReadNews failed:", e);
        if (!alive) return;
        setNews(null);
        setErrMsg(I18N.loadFail[lang]);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, newsType]);

  const homeLabel = I18N.home[lang];
  const infoLabel = I18N.info[lang];

  // breadcrumb label for the type (use URL segment if we don't have mapping)
  const typeLabel = useMemo(() => {
    if (!newsType) return "";

    // Example mapping (adjust based on your actual types)
    const TYPE_MAP = {
      最新消息: {
        ZH: "最新消息",
        EN: "Latest News",
        JP: "ニュース",
      },
      衛生資訊: {
        ZH: "衛生資訊",
        EN: "Health Info",
        JP: "衛生情報",
      },
    };

    const map = TYPE_MAP[newsType];

    if (!map) return newsType; // fallback if not mapped

    if (lang === "JP") return map.JP || map.EN;
    return map.EN || map.ZH;
  }, [newsType, lang]);

  // Render content EXACTLY like WYSIWYG output (read-only)
  const contentHtml = useMemo(() => {
    const html = news?.content || "";
    return rewriteHtmlImageSrc(html);
  }, [news]);

  // Optional: show thumbnail above content (like your other page)
  const thumb = useMemo(() => {
    const u = pickThumbUrl(news);
    return u || "";
  }, [news]);

  if (!loading && !errMsg && !news) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {I18N.notFoundTitle[lang]}
        </Typography>
        <Typography sx={{ mt: 1 }}>{I18N.notFoundHint[lang]}</Typography>
      </Container>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Container maxWidth="lg" sx={{ mt: 2 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          <Link underline="hover" href={"/"} sx={{ color: "text.secondary" }}>
            {homeLabel}
          </Link>

          <Link
            underline="hover"
            href={"/最新資訊"}
            sx={{ color: "text.secondary" }}
          >
            {infoLabel}
          </Link>

          {/* Type (second last segment) */}
          <Link
            underline="hover"
            href={`/最新資訊/${encodeURIComponent(newsType || "")}`}
            sx={{ color: "text.secondary" }}
          >
            {typeLabel}
          </Link>

          <Typography color="text.primary">
            {loading ? "Loading..." : news?.title || slug}
          </Typography>
        </Breadcrumbs>
      </Container>

      {/* Loading / Error */}
      <Container maxWidth="md" sx={{ py: { xs: 2, md: 3 } }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={26} />
          </Box>
        )}
        {!loading && errMsg && <Alert severity="error">{errMsg}</Alert>}
      </Container>

      {!loading && !errMsg && contentHtml && (
        <Container maxWidth="md" sx={{ pb: { xs: 4, md: 8 } }}>
          <Box
            className="seho-news-content"
            sx={{
              "& img": {
                maxWidth: "100%",
                height: "auto",
                display: "block",
                margin: "0 auto",
              },
              "& p": {
                lineHeight: 1.9,
                fontSize: { xs: 15, md: 16 },
              },
            }}
            dangerouslySetInnerHTML={{ __html: contentHtml }}
          />
        </Container>
      )}
    </Box>
  );
}
